import { useSyncExternalStore } from 'react'

type Listener = () => void

class HoverSuppressionStore {
  // Legacy anonymous counter used by call sites that don't pass a key
  private anonCount = 0
  // Optional keyed suppression counts for clearer ownership (e.g. 'palette', 'overlay')
  private keyed = new Map<string, number>()
  private listeners = new Set<Listener>()

  // Backwards‑compatible API: increment/decrement anonymous counter
  suppress(key?: string) {
    if (key) {
      const n = (this.keyed.get(key) || 0) + 1
      this.keyed.set(key, n)
    } else {
      this.anonCount += 1
    }
    this.emit()
  }

  resume(key?: string) {
    if (key) {
      const n = (this.keyed.get(key) || 0) - 1
      if (n <= 0) this.keyed.delete(key)
      else this.keyed.set(key, n)
    } else {
      if (this.anonCount > 0) this.anonCount -= 1
    }
    this.emit()
  }

  // Idempotent toggle by name (preferred for enter/leave semantics)
  set(key: string, active: boolean) {
    if (active) {
      // Ensure present at least once
      if (!this.keyed.has(key)) this.keyed.set(key, 1)
    } else {
      this.keyed.delete(key)
    }
    this.emit()
  }

  reset(key?: string) {
    if (key) {
      if (this.keyed.delete(key)) this.emit()
      return
    }
    let changed = false
    if (this.anonCount !== 0) { this.anonCount = 0; changed = true }
    if (this.keyed.size) { this.keyed.clear(); changed = true }
    if (changed) this.emit()
  }

  isSuppressed() {
    return this.anonCount > 0 || this.keyed.size > 0
  }

  // Expose a numeric snapshot for hooks; sum anon + keyed counts
  getSnapshot = () => {
    let sum = this.anonCount
    for (const n of this.keyed.values()) sum += n
    return sum
  }

  subscribe = (listener: Listener) => {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }

  private emit() {
    for (const listener of this.listeners) listener()
  }
}

export const hoverSuppression = new HoverSuppressionStore()

export function useHoverSuppressed(): boolean {
  const count = useSyncExternalStore(
    hoverSuppression.subscribe,
    hoverSuppression.getSnapshot,
    hoverSuppression.getSnapshot
  )
  return count > 0
}
